import type {
  ListRobotsResponse,
  RobotListSnapshotDto,
  RobotSummaryDto,
  LocationCategory,
  RobotStatus,
  VerificationStatus,
  OwnerEntity,
  AccountingScheme,
} from '@bhops/shared';
import { apiClient } from './client';
import { downloadBlob } from './download';

// S2 Phase 2.7 — 로봇 리스트 정렬. api ListRobotsQuery.sort 와 동일 집합.
// 개선계획 §4 (결정 ⑤B) — shipment_*(첫 출고월 = 이미 나간 기록)와
// shipment_scheduled_*(설치 예정일 = 앞으로 나갈 날)를 분리.
export type RobotListSort =
  | 'shipment_asc'
  | 'shipment_desc'
  | 'shipment_scheduled_asc'
  | 'shipment_scheduled_desc'
  | 'model'
  | 'status'
  | 'created_desc';

/**
 * 표 머리를 눌러 정렬하는 칸(2026-10-01). api ListRobotsQuery.orderBy 와 같은 집합. 값이 있으면
 * 위 정렬 선택(sort)보다 앞선다.
 */
export type RobotListColumn =
  | 'sn'
  | 'model'
  | 'color'
  | 'variant'
  | 'status'
  | 'location'
  | 'firstProductionYm'
  | 'firstShipmentYm'
  | 'customer'
  | 'accountingBookNo';

export interface ListRobotsQuery {
  status?: RobotStatus;
  locationCategory?: LocationCategory;
  verificationStatus?: VerificationStatus;
  modelCode?: string;
  // S2 Phase 2.7 — 제조 facet 필터 (exact-match) + 정렬.
  variantValue?: string;
  sensorType?: string;
  color?: string;
  sort?: RobotListSort;
  orderBy?: RobotListColumn;
  orderDir?: 'asc' | 'desc';
  search?: string;
  /** 이 매장에 지금 있는 로봇(2026-10-28 — 새 조치 이력의 로봇 고르기). */
  storeId?: string;
  // FB3 §2 (batch-15) — 고객 상세 "배정 로봇" 섹션. Robot.reservedContract.
  // customerId 로 join (배치 여부와 무관하게 예약-미배치 로봇 포함).
  reservedForCustomerId?: string;
  page?: number;
  pageSize?: number;
  includeDeleted?: boolean;
  /**
   * 기준일 마감본(plan robot-asof-snapshot-2026-10) — `YYYY-MM-DD`. 있으면 지금 목록 대신 그날 얼린
   * 목록에서 같은 거르기·정렬로 찾는다(목록 · 단계 띠 숫자 · CSV 모두).
   */
  asOf?: string;
}

export interface CreateRobotBody {
  serialShort?: string;
  serialLong?: string;
  modelCode: string;
  status?: RobotStatus;
  locationCategory?: LocationCategory;
  verificationStatus?: VerificationStatus;
  accountingBookNo?: string;
  accountingScheme?: AccountingScheme;
  ownerEntity: OwnerEntity;
  firstProductionYm?: string;
  firstShipmentYm?: string;
  // §D-1 (PDF p.8) — operator-editable trace fields. All optional;
  // nullable on the DB side. ISO date strings (YYYY-MM-DD) for the
  // two timestamps; the API parses them into DateTime.
  // FB2 §5C — loaderType 제거(구분 사양과 중복). DB 컬럼은 보존.
  color?: string;
  manufactureCompletedAt?: string;
  binaryUpdatedAt?: string;
  // round5 #4 — 등록 시 BINARY 펌웨어 이벤트의 버전(선택). 미입력 시 '미상'.
  binaryVersion?: string;
  // S2 Phase 2.2 — 자동 SN Composer 의 내구성 입력. productionDate 는 ISO
  // date (YYYY-MM-DD); 서버가 Date 로 파싱하고 productionWeek(YYWW)를 파생.
  // sensorType 은 OptionGroup `robot.sensorType` 기반 자유 문자열 (F/N).
  productionDate?: string;
  sensorType?: string;
  // FB §2 — 구분 사양(모델 축 값). 선택 모델에 축이 없으면 폼이 미전송.
  variantValue?: string;
  notes?: string;
  // 개선계획 §4 — 등록 창에서 고른 '적용 변경점'(ECN id). 서버가 같은
  // 트랜잭션에서 RobotEcn 을 일괄 생성한다(적용 출처 = 일괄, 적용일 = 오늘).
  ecnIds?: string[];
  // 계획서(2026-08-27) 요구 4 — 등록하면서 함께 장착할 로더(선택).
  loaderUnitId?: string;
}

export interface UpdateRobotBody {
  // §D-1 (PDF p.8) — SN now editable. Server-side uniqueness check
  // remains; clients send the new value. See plan §D-1 #2.
  serialShort?: string;
  serialLong?: string;
  status?: RobotStatus;
  locationCategory?: LocationCategory;
  verificationStatus?: VerificationStatus;
  accountingBookNo?: string;
  accountingScheme?: AccountingScheme;
  color?: string;
  // FB2 §5C — loaderType 제거(구분 사양과 중복). DB 컬럼은 보존.
  // FB §2 — 구분 사양(모델 축 값). 편집 폼에서도 갱신 가능.
  variantValue?: string;
  manufactureCompletedAt?: string;
  binaryUpdatedAt?: string;
  // 초기 데이터 보정 — 첫 제조월 / 첫 출고월(YYMM). 자동 적재분 보정을 위해
  // 관리자 편집 폼에서 갱신 가능(기존엔 생성 시 1회만 세팅).
  firstProductionYm?: string;
  firstShipmentYm?: string;
  notes?: string;
  modelCode?: string;
}

// 라운드15 결정3 — 상태 정정. UpdateRobotBody 와 달리 상태·위치만 받고 사유가
// 필수다(서버가 trim 후 10자 미만이면 400).
export interface CorrectRobotStatusBody {
  status: RobotStatus;
  locationCategory?: LocationCategory;
  reason: string;
}

export const robotsApi = {
  async list(query: ListRobotsQuery = {}): Promise<ListRobotsResponse> {
    const { data } = await apiClient.get<ListRobotsResponse>('/robots', { params: query });
    return data;
  },
  async get(id: string): Promise<RobotSummaryDto> {
    const { data } = await apiClient.get<RobotSummaryDto>(`/robots/${id}`);
    return data;
  },
  async create(body: CreateRobotBody): Promise<RobotSummaryDto> {
    const { data } = await apiClient.post<RobotSummaryDto>('/robots', body);
    return data;
  },
  async update(id: string, body: UpdateRobotBody): Promise<RobotSummaryDto> {
    const { data } = await apiClient.patch<RobotSummaryDto>(`/robots/${id}`, body);
    return data;
  },
  // 라운드15 결정3 — 상태머신을 우회하는 별도 엔드포인트(감사 액션도 분리:
  // robot.status-correction). 일반 편집(update)으로는 전이표가 막는 조합을
  // 저장할 수 없다.
  async correctStatus(id: string, body: CorrectRobotStatusBody): Promise<RobotSummaryDto> {
    const { data } = await apiClient.patch<RobotSummaryDto>(
      `/robots/${id}/status-correction`,
      body,
    );
    return data;
  },
  // 구분 수동 정정(2026-09-01, plan retire-rollback-sale-kind-2026-09 §2-5) —
  // 파생 규칙을 덮는 별도 엔드포인트(감사 액션 robot.contract-kind-correction).
  // kind: null 은 수동값 해제(자동 복귀).
  async correctContractKind(
    id: string,
    body: { kind: 'SALE' | 'SUBSCRIPTION' | null; reason: string },
  ): Promise<RobotSummaryDto> {
    const { data } = await apiClient.patch<RobotSummaryDto>(
      `/robots/${id}/contract-kind-correction`,
      body,
    );
    return data;
  },
  // 사후 계약 연결/해제(2026-09-01 오너 결정 Q2, plan §2-2) — 이미 나가 있거나
  // 판매 완료된 로봇의 장부 연결. 재고 전용 배정 인박스와 별개 경로.
  async linkContract(
    id: string,
    body: { contractId: string; reason: string },
  ): Promise<RobotSummaryDto> {
    const { data } = await apiClient.post<RobotSummaryDto>(`/robots/${id}/contract-link`, body);
    return data;
  },
  async unlinkContract(id: string, body: { reason: string }): Promise<RobotSummaryDto> {
    const { data } = await apiClient.post<RobotSummaryDto>(`/robots/${id}/contract-unlink`, body);
    return data;
  },
  async remove(id: string): Promise<{ id: string; deletedAt: string }> {
    const { data } = await apiClient.delete<{ id: string; deletedAt: string }>(`/robots/${id}`);
    return data;
  },
  // round6 §1-8 — soft-delete 복구.
  async restore(id: string): Promise<{ id: string; deletedAt: string | null }> {
    const { data } = await apiClient.post<{ id: string; deletedAt: string | null }>(
      `/robots/${id}/restore`,
    );
    return data;
  },
  // round6 §1-8 (D11) — 완전 삭제(ADMIN). round7 §1-2 (D3) —
  // includeHistory 면 로봇 자체 이력까지 연쇄 삭제(고객사 데이터는 서버가 거부).
  async permanentDelete(
    id: string,
    includeHistory = false,
  ): Promise<{ id: string; permanentlyDeleted: boolean }> {
    const { data } = await apiClient.delete<{ id: string; permanentlyDeleted: boolean }>(
      `/robots/${id}/permanent`,
      { params: includeHistory ? { includeHistory: 'true' } : undefined },
    );
    return data;
  },
  // 수정 사항2.pdf p.5 #1 — 편집 모달에서 소유 법인 변경 시 호출.
  // /robots/:id/transfer 는 AssetTransferEvent + outbox 이벤트를 atomic
  // 하게 처리하는 ADMIN-only endpoint.
  async transfer(
    id: string,
    body: { toEntity: OwnerEntity; reason: string; docReference?: string },
  ): Promise<{
    id: string;
    fromEntity: OwnerEntity;
    toEntity: OwnerEntity;
    transferEventId: string;
    transferredAt: string;
  }> {
    const { data } = await apiClient.post<{
      id: string;
      fromEntity: OwnerEntity;
      toEntity: OwnerEntity;
      transferEventId: string;
      transferredAt: string;
    }>(`/robots/${id}/transfer`, body);
    return data;
  },
  async exportCsv(query: ListRobotsQuery = {}): Promise<void> {
    // 기준일 마감본이면 한국어 이름에 기준일 — 서버 이름(Content-Disposition, filename*)을 못 읽는 환경의 대체
    // 이름도 같게 둔다(콘솔과 API 가 다른 도메인이라 그 헤더가 숨겨질 수 있다 — 2026-10-08 외부 검토).
    const fallback = query.asOf ? `로봇목록_${query.asOf}_기준.csv` : 'robots.csv';
    await downloadBlob('/robots/export.csv', query, fallback);
  },
};

/** 로봇 기준일 마감본(plan robot-asof-snapshot-2026-10) — 기준일 고르기 목록 · 관리자 '지금 마감'. */
export const robotSnapshotsApi = {
  async list(): Promise<RobotListSnapshotDto[]> {
    const { data } = await apiClient.get<RobotListSnapshotDto[]>('/robot-snapshots');
    return data;
  },
  async capture(): Promise<RobotListSnapshotDto> {
    const { data } = await apiClient.post<RobotListSnapshotDto>('/robot-snapshots');
    return data;
  },
};
